import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { translations } from "../../i18n/translations";
import { useI18n } from "../../i18n/I18nProvider";

/**
 * AdminI18n — content editor for UI strings.
 *
 * Lists all base translation keys (union sv+en) plus any DB overrides.
 * Admin edits a value → upserts via PUT /api/admin/i18n/overrides.
 * "Reset" deletes the override (so the base ships-with value is used again).
 */
export default function AdminI18n({ api, t }) {
  const { refreshOverrides } = useI18n();
  const [overrides, setOverrides] = useState({}); // {`${lang}:${key}`: doc}
  const [activeLang, setActiveLang] = useState("sv");
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState({}); // {`${lang}:${key}`: string}
  const [saving, setSaving] = useState(""); // currently-saving combo

  const allKeys = useMemo(() => {
    const set = new Set([
      ...Object.keys(translations.sv || {}),
      ...Object.keys(translations.en || {}),
    ]);
    return Array.from(set).sort();
  }, []);

  const load = async () => {
    try {
      const r = await api.get("/admin/i18n/overrides");
      const map = {};
      for (const o of (r.data || [])) {
        map[`${o.lang}:${o.key}`] = o;
      }
      setOverrides(map);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dash.failed"));
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const baseValue = (lang, key) => (translations[lang] && translations[lang][key]) || "";
  const effectiveValue = (lang, key) => {
    const ovr = overrides[`${lang}:${key}`];
    return ovr ? ovr.value : baseValue(lang, key);
  };

  const filteredKeys = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return allKeys;
    return allKeys.filter((k) => {
      if (k.toLowerCase().includes(s)) return true;
      const sv = (translations.sv && translations.sv[k]) || "";
      const en = (translations.en && translations.en[k]) || "";
      const ovrSv = overrides[`sv:${k}`]?.value || "";
      const ovrEn = overrides[`en:${k}`]?.value || "";
      return (
        sv.toLowerCase().includes(s) ||
        en.toLowerCase().includes(s) ||
        ovrSv.toLowerCase().includes(s) ||
        ovrEn.toLowerCase().includes(s)
      );
    });
  }, [allKeys, search, overrides]);

  const save = async (key) => {
    const draftKey = `${activeLang}:${key}`;
    const value = drafts[draftKey] ?? effectiveValue(activeLang, key);
    setSaving(draftKey);
    try {
      const r = await api.put("/admin/i18n/overrides", { lang: activeLang, key, value });
      setOverrides((m) => ({ ...m, [draftKey]: r.data }));
      setDrafts((d) => { const { [draftKey]: _drop, ...rest } = d; return rest; });
      toast.success(t("admin.i18n.saved"));
      refreshOverrides();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dash.failed"));
    } finally {
      setSaving("");
    }
  };

  const reset = async (key) => {
    const draftKey = `${activeLang}:${key}`;
    if (!overrides[draftKey]) return;
    setSaving(draftKey);
    try {
      await api.delete(`/admin/i18n/overrides/${activeLang}/${encodeURIComponent(key)}`);
      setOverrides((m) => { const { [draftKey]: _drop, ...rest } = m; return rest; });
      setDrafts((d) => { const { [draftKey]: _drop, ...rest } = d; return rest; });
      toast.success(t("admin.i18n.reset"));
      refreshOverrides();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dash.failed"));
    } finally {
      setSaving("");
    }
  };

  const overrideCount = Object.keys(overrides).length;

  return (
    <div data-testid="admin-i18n">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex gap-px border border-[var(--ink-200)]">
          {["sv", "en"].map((l) => (
            <button
              key={l}
              onClick={() => setActiveLang(l)}
              className={`px-4 py-2 text-xs font-mono-tech uppercase tracking-[0.1em] ${activeLang === l ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`}
              data-testid={`i18n-lang-${l}`}
            >
              {l}
            </button>
          ))}
        </div>
        <input
          className="input-tech flex-1 min-w-[240px] max-w-md"
          placeholder={t("admin.i18n.searchPh")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="i18n-search"
        />
        <div className="label-tech">
          {t("admin.i18n.overrideCount").replace("{n}", String(overrideCount))}
        </div>
      </div>

      <div className="label-tech mb-3">
        {t("admin.i18n.help")}
      </div>

      <div className="border border-[var(--ink-200)] max-h-[70vh] overflow-y-auto divide-y divide-[var(--ink-200)]">
        {filteredKeys.slice(0, 500).map((k) => {
          const composite = `${activeLang}:${k}`;
          const isOverridden = !!overrides[composite];
          const currentEff = effectiveValue(activeLang, k);
          const draftVal = drafts[composite] ?? currentEff;
          const isDirty = (drafts[composite] !== undefined) && (drafts[composite] !== currentEff);
          return (
            <div key={k} className="p-3 bg-[var(--surface)]" data-testid={`i18n-row-${k}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <code className="text-xs text-[var(--ink-700)]">{k}</code>
                <div className="flex items-center gap-2">
                  {isOverridden && <span className="tag tag-clay text-[10px]">{t("admin.i18n.overridden")}</span>}
                </div>
              </div>
              <div className="label-tech mb-1">
                {t("admin.i18n.base")}: <span className="text-[var(--ink-700)]">{baseValue(activeLang, k) || "—"}</span>
              </div>
              <textarea
                rows={Math.min(4, Math.max(1, Math.ceil(draftVal.length / 80)))}
                value={draftVal}
                onChange={(e) => setDrafts((d) => ({ ...d, [composite]: e.target.value }))}
                className="input-tech text-sm"
                data-testid={`i18n-input-${k}`}
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => save(k)}
                  className="btn-primary text-xs"
                  disabled={saving === composite || !isDirty}
                  data-testid={`i18n-save-${k}`}
                >
                  {saving === composite ? "…" : t("admin.i18n.save")}
                </button>
                {isOverridden && (
                  <button
                    onClick={() => reset(k)}
                    className="btn-ghost text-xs"
                    disabled={saving === composite}
                    data-testid={`i18n-reset-${k}`}
                  >
                    {t("admin.i18n.resetBtn")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {filteredKeys.length > 500 && (
          <div className="p-3 label-tech">{t("admin.i18n.truncated").replace("{n}", String(filteredKeys.length))}</div>
        )}
      </div>
    </div>
  );
}
